import React, { useState } from "react";
import Icon from "../AppIcon";

const TOUR_STEPS = [
  {
  title: "Dashboard Overview",
    description:
      "Your main hub — see today's TRONC totals, pending distributions, eligible employees, and compliance status at a glance.",
    icon: "LayoutDashboard",
    color: "text-primary",
    tip: "The summary cards update in real time as data syncs from your integrations.",
  },
  {
    title: "Daily Takings & TRONC",
    description:
   "Record and review daily TRONC takings for each location. Data can be entered manually or synced automatically from your Till system.",
    icon: "PoundSterling",
    color: "text-[#FF5A5F]",
    tip: "Use the date picker to navigate between days and the location filter to focus on specific venues.",
  },
  {
    title: "TRONC Distribution",
    description:
      "Allocate TRONC fairly to employees based on your configured rules — by role weighting, hours worked, equal split, or custom methods.",
    icon: "PieChart",
    color: "text-secondary",
    tip: "Draft distributions can be reviewed before approval. Once approved, records are locked for HMRC compliance.",
  },
  {
    title: "Employee Management",
    description:
      "View and manage all employees across your locations. Set TRONC eligibility, job roles, and seniority bands.",
    icon: "Users",
    color: "text-primary",
    tip: "Employees can be synced from your HR system or added manually. Use filters to find specific employees quickly.",
  },
  {
    title: "Distribution Rules",
    description:
      "Configure how TRONC is divided — set weighting by job role, seniority multipliers, and minimum hours thresholds.",
    icon: "Settings",
    color: "text-accent",
    tip: "Rules can have effective dates so you can prepare future changes without affecting current distributions.",
  },
  {
    title: "Audit Trail",
    description:
      "Every action in the system is recorded with a full audit trail — who did what, when, and the before/after values.",
    icon: "FileText",
    color: "text-success",
    tip: "The audit trail is essential for HMRC compliance and resolving any queries about distribution decisions.",
  },
  {
    title: "Integrations",
    description:
      "Connect your HR and Till systems to automatically sync employees, clock records, locations, and TRONC data.",
    icon: "Link",
    color: "text-secondary",
 tip: "Go to Settings → Company or System → Integrations to configure and run syncs.",
  },
  {
    title: "Need Help?",
    description:
   "Click the help icon in the bottom-right corner of any page to access contextual help articles and guides.",
    icon: "HelpCircle",
    color: "text-primary",
    tip: "You can also access all help documentation from the sidebar under 'Help & Support'.",
  },
];

const TOUR_STORAGE_KEY = "rocket_tronc_tour_complete";

export const shouldShowTour = () => {
  return !localStorage.getItem(TOUR_STORAGE_KEY);
};

export const markTourComplete = () => {
  localStorage.setItem(TOUR_STORAGE_KEY, "true");
};

const WelcomeTour = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const step = TOUR_STEPS[currentStep];
  const isLast = currentStep === TOUR_STEPS.length - 1;

  const handleFinish = () => {
    markTourComplete();
    onComplete();
  };

  const handleSkip = () => {
    markTourComplete();
    onComplete();
  };

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md mx-4">
        {/* Progress dots */}
     <div className="flex items-center justify-center gap-1.5 mb-4">
          {TOUR_STEPS.map((_, i) => (
 <div
        key={i}
         className={`h-1.5 rounded-full transition-all ${
      i === currentStep
      ? "w-6 bg-primary"
           : i < currentStep
          ? "w-1.5 bg-primary/50"
        : "w-1.5 bg-white/30"
            }`}
     />
          ))}
        </div>

{/* Card */}
 <div className="bg-card rounded-2xl shadow-2xl border border-border overflow-hidden">
 {/* Icon header */}
          <div className="bg-gradient-to-br from-[#1a2b4b] to-[#2d4a7c] p-8 text-center">
   <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-4">
          <Icon name={step.icon} size={32} className="text-white" />
       </div>
            <h2 className="text-xl font-heading font-bold text-white">
      {step.title}
  </h2>
            <p className="text-sm text-white/60 mt-1">
         Step {currentStep + 1} of {TOUR_STEPS.length}
            </p>
    </div>

  {/* Content */}
        <div className="p-6">
 <p className="text-foreground mb-4">{step.description}</p>

            {step.tip && (
       <div className="flex items-start gap-2 p-3 bg-primary/5 rounded-lg">
        <Icon
     name="Lightbulb"
     size={16}
       className="text-primary mt-0.5 shrink-0"
        />
     <p className="text-sm text-muted-foreground">{step.tip}</p>
              </div>
     )}
          </div>

      {/* Actions */}
          <div className="flex items-center justify-between p-4 border-t border-border">
          <button
            onClick={handleSkip}
           className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
      Skip tour
     </button>

     <div className="flex items-center gap-2">
            {currentStep > 0 && (
    <button
                  onClick={() => setCurrentStep((s) => s - 1)}
    className="btn btn-ghost btn-sm gap-1"
     >
      <Icon name="ArrowLeft" size={14} />
                  Back
     </button>
    )}
          <button
                onClick={
         isLast
            ? handleFinish
        : () => setCurrentStep((s) => s + 1)
            }
                className="btn btn-primary btn-sm gap-1"
              >
 {isLast ? "Get Started!" : "Next"}
       {!isLast && <Icon name="ArrowRight" size={14} />}
         </button>
     </div>
  </div>
   </div>
  </div>
  </div>
  );
};

export default WelcomeTour;
